import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Tooltip } from '@mui/material';

interface ITableCellContentProps {
  children?: ReactNode;
  maxWidth?: number | string;
}

const getTooltipTitle = (children?: ReactNode) => {
  if (children === null || children === undefined) {
    return '';
  }

  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  return '';
};

const TableCellContent: React.FC<ITableCellContentProps> = ({
  children,
  maxWidth = '100%',
}) => {
  const title = getTooltipTitle(children);
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const checkOverflow = useCallback(() => {
    const element = contentRef.current;

    if (!element) {
      setIsOverflowing(false);
      return;
    }

    setIsOverflowing(element.scrollWidth > element.clientWidth);
  }, []);

  useEffect(() => {
    checkOverflow();
  }, [checkOverflow, children, maxWidth]);

  useEffect(() => {
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow]);

  return (
    <Tooltip
      arrow
      disableHoverListener={!title || !isOverflowing}
      placement={'top'}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: 20,
            lineHeight: 1.35,
          },
        },
      }}
      title={title}
    >
      <Box
        ref={contentRef}
        component={'span'}
        sx={{
          display: 'block',
          lineHeight: 1.35,
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export default TableCellContent;
